import { Outlet } from "@tanstack/react-router"
import { ReactNode, useMemo } from "react"
import { RouteTabs, RouteTrigger } from "../../molecules/RouteTabs"
import { PageLayout } from "../../organisms/PageLayout"

interface AuthProps {
  pathname: string
  isInvite: boolean
  isVerification: boolean
}

export const LoginDescription = () => {
  return (
    <div className="flex flex-col gap-2">
      <h1 className="text-2xl">Welcome back</h1>
      <p className="text-lg">Sign in to your account</p>
    </div>
  )
}

export const RegisterSubDescription = () => (
  <div className="flex flex-col gap-2">
    <p className="text-sm italic">
      Trial accounts are only available for registered companies and organizations. Trial accounts are limited to 10
      simultaneous viewers. AV1 transcoding is not available for trial accounts.
    </p>
  </div>
)

export const RegisterTopDescription = ({
  isVerification,
  isInvite,
}: {
  isVerification?: boolean
  isInvite?: boolean
}) => {
  if (isVerification) {
    return (
      <div className="flex flex-col gap-2">
        <h1 className="mb-2 text-2xl font-medium">Email verified</h1>
        <p className="text-lg">Choose a username and password to complete your registration.</p>
      </div>
    )
  }

  if (isInvite) {
    return (
      <div className="flex flex-col gap-2">
        <h1 className="mb-2 text-2xl font-medium">You have been invited to join Vindral</h1>
        <p className="text-lg">
          Choose a username and password to complete your registration in order to accept the invitation.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div>
        <h1 className="mb-2 text-2xl font-medium">Create a free trial account</h1>
        <p className="text-lg">
          Stream your content in high quality with ultra-low latency and synchronized playout to all devices.
        </p>
      </div>
      <ul className="list-inside list-disc text-lg">
        <li>No credit card needed</li>
        <li>No commitment</li>
      </ul>
    </div>
  )
}

const RegisterDesktop = ({ isVerification, isInvite }: { isVerification: boolean; isInvite: boolean }) => (
  <>
    <RegisterTopDescription isVerification={isVerification} isInvite={isInvite} />
    {!isVerification && !isInvite && <RegisterSubDescription />}
  </>
)

export const ForgotPasswordDescription = () => {
  return (
    <div className="flex flex-col gap-2">
      <h1 className="text-2xl">Forgot password?</h1>
      <p className="text-base lg:text-lg">
        Enter the email address associated with your account and we will send you a link to reset your password
      </p>
    </div>
  )
}

export const Auth = (props: AuthProps) => {
  const { pathname } = props

  const authPathDescriptionMapping: Record<string, ReactNode> = useMemo(
    () => ({
      "/auth/login": <LoginDescription />,
      "/auth/register": <RegisterDesktop isVerification={props.isVerification} isInvite={props.isInvite} />,
      "/auth/forgot-password": <ForgotPasswordDescription />,
    }),
    [props.isVerification, props.isInvite]
  )

  return (
    <>
      <div className="hidden lg:block">
        <PageLayout description={authPathDescriptionMapping[pathname]}>
          <div className="max-w-md pl-8">
            <RouteTabs border="none">
              <RouteTrigger to="/auth/register" search border="none">
                REGISTER
              </RouteTrigger>
              <RouteTrigger to="/auth/login" search border="none">
                SIGN IN
              </RouteTrigger>
              <RouteTrigger to="/auth/forgot-password" search border="none">
                FORGOT PASSWORD
              </RouteTrigger>
            </RouteTabs>
            <Outlet />
          </div>
        </PageLayout>
      </div>
      <div className="mb-4 block w-full px-4 lg:hidden">
        <div className="mx-auto max-w-md">
          <Outlet />
        </div>
      </div>
    </>
  )
}
